import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'

// Layout
import { ScrollToTop, ErrorBoundary } from '@/components/layout'
import PublicLayout from '@/layouts/PublicLayout'
import AuthLayout from '@/layouts/AuthLayout'
import AdminLayout from '@/layouts/AdminLayout'
import AppLayout from '@/layouts/AppLayout'

// Auth
import { ProtectedRoute, AdminRoute, Login } from '@/components/auth'

// Landing
import {
  VideoHero,
  MissionFounder,
  ProductBento,
  LandingFAQ,
  SignupCTA,
  About,
} from '@/components/landing'

// Shared
import { CookieConsentBanner, OfflineBanner } from '@/components/shared'

// Forms
import { FeedbackForm } from '@/components/forms'

// Onboarding
import { Onboarding, Welcome, PaymentSuccess } from '@/components/onboarding'

// Pages — small public pages stay eager so NotFound / Privacy / Terms render instantly
import Privacy from '@/pages/Privacy'
import Terms from '@/pages/Terms'
import HowItWorks from '@/pages/HowItWorks'
import Subscribe from '@/pages/Subscribe'
import Support from '@/pages/Support'
import ResetPassword from '@/pages/ResetPassword'
import ChangelogPage from '@/pages/ChangelogPage'
import RedeemCode from '@/pages/RedeemCode'
import NotFound from '@/pages/NotFound'

// Lazy-loaded app pages — never needed on the landing page
const HomeDashboard = lazy(() => import('@/pages/HomeDashboard'))
const Learn = lazy(() => import('@/pages/Learn'))
const LearnPath = lazy(() => import('@/pages/LearnPath'))
const LearnLesson = lazy(() => import('@/pages/LearnLesson'))
const LearnAchievements = lazy(() => import('@/pages/LearnAchievements'))
const PrayerGenerator = lazy(() => import('@/pages/PrayerGenerator'))
const PrayerTopicPage = lazy(() => import('@/pages/PrayerTopicPage'))
const BiblePage = lazy(() => import('@/pages/BiblePage'))
const AccountPage = lazy(() => import('@/pages/AccountPage'))
const JourneyCardPage = lazy(() => import('@/pages/JourneyCardPage'))
const MinistryMap = lazy(() => import('@/pages/MinistryMap'))
const People = lazy(() => import('@/pages/People'))
const PersonProfile = lazy(() => import('@/pages/PersonProfile'))
const More = lazy(() => import('@/pages/More'))
const Saved = lazy(() => import('@/pages/Saved'))

// Lazy-loaded admin pages — tiny % of traffic, no reason to ship to landing visitors
const SectionEditor = lazy(() => import('@/pages/admin/SectionEditor'))
const InviteCodesAdmin = lazy(() => import('@/pages/admin/InviteCodesAdmin'))
const ChatAnalyticsAdmin = lazy(() => import('@/pages/admin/ChatAnalyticsAdmin'))
const PatchNotesAdmin = lazy(() => import('@/pages/admin/PatchNotesAdmin'))
const FeedbackAdmin = lazy(() => import('@/pages/admin/FeedbackAdmin'))
const UsersAdmin = lazy(() => import('@/pages/admin/UsersAdmin'))
const SupportAdmin = lazy(() => import('@/pages/admin/SupportAdmin'))
const RoadmapAdmin = lazy(() => import('@/pages/admin/RoadmapAdmin'))
const BlogPipelineAdmin = lazy(() => import('@/pages/admin/BlogPipelineAdmin'))
const FeaturesAdmin = lazy(() => import('@/pages/admin/FeaturesAdmin'))
const BlogAdmin = lazy(() => import('@/pages/admin/BlogAdmin'))
const BlogEditor = lazy(() => import('@/pages/admin/BlogEditor'))
const BlogPreview = lazy(() => import('@/pages/admin/BlogPreview'))
const BibleHeadingsAdmin = lazy(() => import('@/pages/admin/BibleHeadingsAdmin'))
const ContentLinksAdmin = lazy(() => import('@/pages/admin/ContentLinksAdmin'))
const LearnReview = lazy(() => import('@/pages/admin/LearnReview'))
const PeopleReview = lazy(() => import('@/pages/admin/PeopleReview'))
const DemoPreview = lazy(() => import('@/pages/admin/DemoPreview'))
const OnboardingPreview = lazy(() => import('@/pages/admin/OnboardingPreview'))

// Lazy-loaded heavy pages
const ChatV2 = lazy(() => import('@/pages/ChatV2'))
const Share = lazy(() => import('@/pages/Share'))
const BlogIndex = lazy(() => import('@/pages/BlogIndex'))
const BlogPost = lazy(() => import('@/pages/BlogPost'))
const Pricing = lazy(() => import('@/components/landing/Pricing'))
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminFinance = lazy(() => import('@/pages/admin/AdminFinance'))
const AdminAIOps = lazy(() => import('@/pages/admin/AdminAIOps'))
const AdminContent = lazy(() => import('@/pages/admin/AdminContent'))
const AdminSystemHealth = lazy(() => import('@/pages/admin/AdminSystemHealth'))
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'))
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'))
const AIAutomationsPage = lazy(() => import('@/pages/admin/AIAutomationsPage'))
const StudioAdmin = lazy(() => import('@/pages/admin/StudioAdmin'))
const Capture = lazy(() => import('@/pages/Capture'))

// Landing redesign mockup — only the opening video remains.
// noindex'd, public-but-unlinked. See briefs/landing-redesign-mockups.md.
const PreviewIndex = lazy(() => import('@/pages/preview/Index'))
const PreviewOpeningVideo = lazy(() => import('@/pages/preview/OpeningVideo'))

const LazyFallback = (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-2 border-[var(--gold)] border-t-transparent rounded-full" />
  </div>
)

// Hide the cookie banner on /preview/* routes — those pages are internal
// mockups, the banner clutters the bottom of the otherwise-minimal canvas.
//
// DisclaimerModal retired 2026-05-17 (Mason): V7 onboarding step 5
// ("Where Illuminate stands" / doctrinal-trust) now covers the first-load
// disclaimer content as part of the funnel, so the modal-on-first-visit
// pattern is redundant. Component file preserved at
// src/components/shared/DisclaimerModal.jsx in case we ever want to
// re-mount; the barrel export stays so other consumers (none currently)
// don't break.
function GlobalOverlays() {
  const location = useLocation()
  if (location.pathname.startsWith('/preview')) return null
  return (
    <>
      <CookieConsentBanner />
    </>
  )
}

function PageHelmet({ title, description, path, noindex }) {
  const fullTitle = `${title} — Illuminate Faith`
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`https://illuminatefaithai.com${path}`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://illuminatefaithai.com/FinalLogo.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://illuminatefaithai.com/FinalLogo.png" />
      {!noindex && <link rel="canonical" href={`https://illuminatefaithai.com${path}`} />}
    </Helmet>
  )
}

function LandingPage() {
  // Signed-in onboarded users skip the public landing and land on /home
  // instead — the in-app dashboard is "home" for them. While session/profile
  // is still loading, render the landing as-is; the redirect kicks in once
  // they resolve.
  //
  // Escape hatch: `?from=app` (used by the in-app + admin logos) bypasses the
  // redirect so signed-in users can intentionally view the public marketing
  // landing — bookmark `/?from=app` for dev iteration on the hero.
  const { user, loading: sessionLoading } = useSession()
  const { profile } = useProfile()
  const fromApp = new URLSearchParams(window.location.search).get('from') === 'app'
  if (!sessionLoading && user && profile?.onboardingCompleted && !fromApp) {
    return <Navigate to="/home" replace />
  }

  return (
    <>
      <Helmet>
        <title>Illuminate Faith — Christian AI for Scripture, Apologetics &amp; Bible Study</title>
        <meta
          name="description"
          content="Illuminate Faith equips Christians with intelligent tools for apologetics answers, AI-powered Scripture commentary, and Bible study prep — grounded in sound theology."
        />
        <meta
          property="og:title"
          content="Illuminate Faith — Christian AI for Scripture, Apologetics &amp; Bible Study"
        />
        <meta
          property="og:description"
          content="Intelligent tools for apologetics, Scripture commentary, and Bible study prep — grounded in sound theology."
        />
        <meta property="og:url" content="https://illuminatefaithai.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://illuminatefaithai.com/FinalLogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://illuminatefaithai.com/FinalLogo.png" />
        <link rel="canonical" href="https://illuminatefaithai.com" />
      </Helmet>
      <VideoHero />
      <MissionFounder />
      <ProductBento />
      <LandingFAQ />
      <SignupCTA />
    </>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <div className="font-body">
        {/* Skip-to-content link — first focusable element on every page.
            Invisible until keyboard-focused, then pops into view as a gold
            pill in the top-left. Pressing Enter jumps the cursor past the
            navbar to the main content. WCAG 2.2 SC 2.4.1 (Bypass Blocks). */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10000] focus:px-4 focus:py-2 focus:rounded-md focus:bg-[var(--gold)] focus:text-[var(--text-on-gold)] focus:font-semibold focus:shadow-lg"
        >
          Skip to main content
        </a>
        <ScrollToTop />
        <OfflineBanner />
        <GlobalOverlays />
        <Suspense fallback={LazyFallback}>
          <Routes>
            {/* Landing redesign mockups — public but unlinked, noindex'd. No
                PublicLayout wrap so the heavy navbar/footer don't bleed into
                the minimal mockup chrome. Each page owns its own MockupShell. */}
            <Route
              path="/preview"
              element={
                <ErrorBoundary>
                  <PreviewIndex />
                </ErrorBoundary>
              }
            />
            <Route
              path="/preview/opening-video"
              element={
                <ErrorBoundary>
                  <PreviewOpeningVideo />
                </ErrorBoundary>
              }
            />

            {/* Public pages with Navbar + Footer */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/privacy"
                element={
                  <>
                    <PageHelmet
                      title="Privacy Policy"
                      description="How Illuminate Faith protects your data and respects your privacy."
                      path="/privacy"
                    />
                    <Privacy />
                  </>
                }
              />
              <Route
                path="/terms"
                element={
                  <>
                    <PageHelmet
                      title="Terms of Service"
                      description="Terms and conditions for using Illuminate Faith."
                      path="/terms"
                    />
                    <Terms />
                  </>
                }
              />
              <Route
                path="/how-it-works"
                element={
                  <>
                    <PageHelmet
                      title="How It Works"
                      description="How Illuminate Faith handles theology, what the AI does and doesn't do, and what happens to your data."
                      path="/how-it-works"
                    />
                    <HowItWorks />
                  </>
                }
              />
              <Route
                path="/pricing"
                element={
                  <Suspense fallback={LazyFallback}>
                    <PageHelmet
                      title="Pricing"
                      description="Free and Premium plans for Illuminate Faith. Founding Member pricing locks in our lowest annual rate forever — limited spots."
                      path="/pricing"
                    />
                    <Pricing />
                  </Suspense>
                }
              />
              <Route path="/churches" element={<Navigate to="/" replace />} />
              <Route
                path="/about"
                element={
                  <>
                    <PageHelmet
                      title="About"
                      description="Learn about the mission, values, and story behind Illuminate Faith — Christian AI built with purpose."
                      path="/about"
                    />
                    <About />
                  </>
                }
              />
              <Route
                path="/subscribe"
                element={
                  <>
                    <PageHelmet
                      title="Subscribe"
                      description="Subscribe to Illuminate Faith for unlimited access to all AI tools."
                      path="/subscribe"
                      noindex
                    />
                    <Subscribe />
                  </>
                }
              />
              <Route
                path="/feedback"
                element={
                  <>
                    <PageHelmet
                      title="Feedback"
                      description="Share your feedback to help us improve Illuminate Faith."
                      path="/feedback"
                    />
                    <FeedbackForm />
                  </>
                }
              />
              <Route
                path="/support"
                element={
                  <>
                    <PageHelmet
                      title="Support"
                      description="Get help from the Illuminate Faith team — contact us with questions, issues, or feedback."
                      path="/support"
                    />
                    <Support />
                  </>
                }
              />
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/payment-success"
                  element={
                    <>
                      <PageHelmet
                        title="Payment Successful"
                        description="Your subscription to Illuminate Faith is now active."
                        path="/payment-success"
                        noindex
                      />
                      <PaymentSuccess />
                    </>
                  }
                />
              </Route>
            </Route>

            {/* App shell — logged-in feature pages */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route
                  path="/home"
                  element={
                    <>
                      <PageHelmet
                        title="Home"
                        description="Your daily faith dashboard — devotionals, Bible reading, and spiritual growth."
                        path="/home"
                        noindex
                      />
                      <HomeDashboard />
                    </>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <PageHelmet
                        title="Chat"
                        description="Chat with AI grounded in Scripture — multi-voice study with Grace, Logos, Spurgeon, and Ezra."
                        path="/chat"
                      />
                      <ChatV2 />
                    </Suspense>
                  }
                />
                <Route
                  path="/learn"
                  element={
                    <>
                      <PageHelmet
                        title="Learn"
                        description="Structured learning paths for Scripture, theology, and apologetics."
                        path="/learn"
                        noindex
                      />
                      <Learn />
                    </>
                  }
                />
                <Route
                  path="/learn/achievements"
                  element={
                    <>
                      <PageHelmet
                        title="Achievements"
                        description="Your trophy case of every learning path you've mastered on Illuminate Faith."
                        path="/learn/achievements"
                        noindex
                      />
                      <LearnAchievements />
                    </>
                  }
                />
                <Route path="/learn/:pathSlug" element={<LearnPath />} />
                <Route path="/learn/:pathSlug/:lessonSlug" element={<LearnLesson />} />
                <Route
                  path="/prayer"
                  element={
                    <>
                      <PageHelmet
                        title="Prayer"
                        description="AI-powered prayer generator — personalized prayers with Scripture."
                        path="/prayer"
                        noindex
                      />
                      <PrayerGenerator />
                    </>
                  }
                />
                <Route
                  path="/prayer/topic/:topicId"
                  element={
                    <>
                      <PageHelmet
                        title="Prayer Topic"
                        description="Your prayer topic timeline — every prayer, every answer."
                        path="/prayer/topic"
                        noindex
                      />
                      <PrayerTopicPage />
                    </>
                  }
                />
                <Route
                  path="/bible"
                  element={
                    <>
                      <PageHelmet
                        title="Bible"
                        description="Read Scripture with AI insights, highlights, notes, and reading plans."
                        path="/bible"
                        noindex
                      />
                      <BiblePage />
                    </>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <>
                      <PageHelmet
                        title="Account"
                        description="Manage your Illuminate Faith subscription and account settings."
                        path="/account"
                        noindex
                      />
                      <AccountPage />
                    </>
                  }
                />
                <Route
                  path="/account/journey"
                  element={
                    <>
                      <PageHelmet
                        title="Your Journey"
                        description="Your trading card — every stat from your walk with Illuminate."
                        path="/account/journey"
                        noindex
                      />
                      <JourneyCardPage />
                    </>
                  }
                />
                <Route
                  path="/people"
                  element={
                    <>
                      <PageHelmet
                        title="People"
                        description="A tappable encyclopedia of every significant Bible character — story, why they matter, key verses, and family ties."
                        path="/people"
                        noindex
                      />
                      <People />
                    </>
                  }
                />
                <Route
                  path="/people/:slug"
                  element={
                    <>
                      <PageHelmet
                        title="Person"
                        description="A profile of a Bible character — story, why they matter, key verses, and family ties."
                        path="/people"
                        noindex
                      />
                      <PersonProfile />
                    </>
                  }
                />
                <Route
                  path="/ministry-map"
                  element={
                    <>
                      <PageHelmet
                        title="Ministry Map"
                        description="Your private map of people to pray for, invest in, and walk alongside."
                        path="/ministry-map"
                        noindex
                      />
                      <MinistryMap />
                    </>
                  }
                />
                <Route
                  path="/more"
                  element={
                    <>
                      <PageHelmet
                        title="More"
                        description="Account, achievements, patch notes, and the reference and ministry tools that grow with you."
                        path="/more"
                        noindex
                      />
                      <More />
                    </>
                  }
                />
                <Route
                  path="/saved"
                  element={
                    <>
                      <PageHelmet
                        title="Saved messages"
                        description="Every AI reply you've bookmarked across your conversations."
                        path="/saved"
                        noindex
                      />
                      <Saved />
                    </>
                  }
                />
              </Route>
            </Route>

            {/* Auth pages with minimal layout */}
            <Route element={<AuthLayout />}>
              <Route
                path="/login"
                element={
                  <>
                    <PageHelmet
                      title="Sign In"
                      description="Sign in or create your free Illuminate Faith account."
                      path="/login"
                      noindex
                    />
                    <Login />
                  </>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <>
                    <PageHelmet
                      title="Reset Password"
                      description="Reset your Illuminate Faith password."
                      path="/reset-password"
                      noindex
                    />
                    <ResetPassword />
                  </>
                }
              />
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/onboarding"
                  element={
                    <>
                      <PageHelmet
                        title="Onboarding"
                        description="Set up your Illuminate Faith profile and preferences."
                        path="/onboarding"
                        noindex
                      />
                      <Onboarding />
                    </>
                  }
                />
                <Route
                  path="/welcome"
                  element={
                    <>
                      <PageHelmet
                        title="Welcome"
                        description="Welcome to Illuminate Faith — your faith journey starts here."
                        path="/welcome"
                        noindex
                      />
                      <Welcome />
                    </>
                  }
                />
                <Route
                  path="/redeem"
                  element={
                    <>
                      <PageHelmet
                        title="Redeem Code"
                        description="Redeem your invite code for Illuminate Faith."
                        path="/redeem"
                        noindex
                      />
                      <RedeemCode />
                    </>
                  }
                />
              </Route>
            </Route>

            {/* Admin pages */}
            <Route element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route
                  path="/admin"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/finance"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminFinance />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/ai-ops"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminAIOps />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/content"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminContent />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/system-health"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminSystemHealth />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/automations"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AIAutomationsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/analytics"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminAnalytics />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin/reports"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <AdminReports />
                    </Suspense>
                  }
                />
                <Route path="/admin/sections/:sectionKey" element={<SectionEditor />} />
                <Route path="/admin/beta-invites" element={<InviteCodesAdmin />} />
                <Route path="/admin/chat-analytics" element={<ChatAnalyticsAdmin />} />
                <Route path="/admin/patch-notes" element={<PatchNotesAdmin />} />
                <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                <Route path="/admin/users" element={<UsersAdmin />} />
                <Route path="/admin/support" element={<SupportAdmin />} />
                <Route path="/admin/blog" element={<BlogAdmin />} />
                <Route path="/admin/blog/:id" element={<BlogEditor />} />
                <Route path="/admin/blog/:id/preview" element={<BlogPreview />} />
                <Route path="/admin/roadmap" element={<RoadmapAdmin />} />
                <Route path="/admin/blog-pipeline" element={<BlogPipelineAdmin />} />
                <Route path="/admin/features" element={<FeaturesAdmin />} />
                <Route path="/admin/bible-headings" element={<BibleHeadingsAdmin />} />
                <Route path="/admin/links" element={<ContentLinksAdmin />} />
                <Route path="/admin/learn/review" element={<LearnReview />} />
                <Route path="/admin/people/review" element={<PeopleReview />} />
                <Route path="/admin/demos" element={<DemoPreview />} />
                <Route path="/admin/onboarding-preview" element={<OnboardingPreview />} />
                <Route
                  path="/admin/studio"
                  element={
                    <Suspense fallback={LazyFallback}>
                      <StudioAdmin />
                    </Suspense>
                  }
                />
              </Route>
              {/* Mobile capture — admin-auth gated but NO sidebar chrome
                  (full-bleed parchment surface). Bookmarkable as a
                  home-screen icon for thumb-typing on the go. */}
              <Route
                path="/capture"
                element={
                  <Suspense fallback={LazyFallback}>
                    <Capture />
                  </Suspense>
                }
              />
            </Route>

            {/* Blog — public */}
            <Route element={<PublicLayout />}>
              <Route
                path="/blog"
                element={
                  <Suspense fallback={LazyFallback}>
                    <BlogIndex />
                  </Suspense>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <Suspense fallback={LazyFallback}>
                    <BlogPost />
                  </Suspense>
                }
              />
            </Route>

            {/* Public changelog — readable by anyone */}
            <Route element={<PublicLayout />}>
              <Route
                path="/changelog"
                element={
                  <>
                    <PageHelmet
                      title="Changelog"
                      description="See what's new in Illuminate Faith — latest features, improvements, and updates."
                      path="/changelog"
                    />
                    <ChangelogPage />
                  </>
                }
              />
            </Route>

            {/* Public share view — no layout, no auth required */}
            <Route
              path="/share/:token"
              element={
                <Suspense fallback={LazyFallback}>
                  <Share />
                </Suspense>
              }
            />

            {/* 404 catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}
